// Reads the session's currentRoleContext and renders the matching shell.
// - No context yet + exactly one app-renderable role → auto-select it.
// - No context yet + multiple roles → role picker.
// - Context set → dispatch to the parent / kid / teacher / school shell.
//
// Staff-only roles (super_admin, content_editor, support) have no consumer
// shell — those users belong in the admin app, so we show a gentle notice.

import { useT } from '@fajrarabi/i18n/react';
import { Button } from '@fajrarabi/ui';
import { useEffect, type ReactNode } from 'react';

import { AppSplash } from './AppSplash';
import { KidShell } from './shells/KidShell';
import { ParentShell } from './shells/ParentShell';
import { SchoolAdminShell } from './shells/SchoolAdminShell';
import { TeacherShell } from './shells/TeacherShell';

import { useAuth } from '~/lib/auth';
import { appRoleContexts, roleLabelKey, shellForContext } from '~/lib/roles';

export function RoleAwareShell({ children }: { children: ReactNode }) {
  const { currentRoleContext, availableRoleContexts, setRole } = useAuth();
  const contexts = appRoleContexts(availableRoleContexts);

  const activeShell = shellForContext(currentRoleContext);

  // Auto-select when there's a single app role and the session hasn't picked
  // one yet (e.g. after a plain login, which leaves currentRoleContext null).
  const soleContext = contexts.length === 1 ? contexts[0] : null;
  useEffect(() => {
    if (!activeShell && soleContext) {
      void setRole(soleContext);
    }
  }, [activeShell, soleContext, setRole]);

  // Context resolves to a renderable shell → render it.
  if (activeShell) {
    switch (activeShell) {
      case 'parent':
        return <ParentShell>{children}</ParentShell>;
      case 'kid':
        return <KidShell>{children}</KidShell>;
      case 'teacher':
        return <TeacherShell>{children}</TeacherShell>;
      case 'school_admin':
        return <SchoolAdminShell>{children}</SchoolAdminShell>;
    }
  }

  // Multiple roles, none chosen → role picker.
  if (contexts.length > 1) {
    return <RolePicker contexts={contexts} onPick={(c) => void setRole(c)} />;
  }

  // Single role: the effect above is mid-flight → splash.
  if (contexts.length === 1) {
    return <AppSplash />;
  }

  // No app-renderable role at all (staff-only account).
  return <NoConsumerRole />;
}

function RolePicker({
  contexts,
  onPick,
}: {
  contexts: string[];
  onPick: (context: string) => void;
}) {
  const t = useT();
  return (
    <div className="grid min-h-dvh place-items-center bg-cream-50 p-6">
      <div className="w-full max-w-sm rounded-3xl bg-white p-8 text-center shadow-[0_24px_60px_-30px_rgba(31,26,23,0.18)] ring-1 ring-burgundy-500/10">
        <h1 className="text-xl font-bold text-ink-900">{t('app.common.pick_role_title')}</h1>
        <p className="mt-1 text-sm text-ink-700">{t('app.common.pick_role_subtitle')}</p>
        <div className="mt-6 space-y-3">
          {contexts.map((ctx) => (
            <Button key={ctx} fullWidth onClick={() => onPick(ctx)}>
              {t(roleLabelKey(ctx))}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

function NoConsumerRole() {
  const t = useT();
  const { signOut } = useAuth();
  return (
    <div className="grid min-h-dvh place-items-center bg-cream-50 p-6">
      <div className="w-full max-w-sm rounded-3xl bg-white p-8 text-center shadow-[0_24px_60px_-30px_rgba(31,26,23,0.18)] ring-1 ring-burgundy-500/10">
        <p className="text-sm text-ink-700">{t('app.common.error_generic')}</p>
        <Button variant="secondary" className="mt-6" onClick={() => void signOut()}>
          {t('app.common.sign_out')}
        </Button>
      </div>
    </div>
  );
}
